import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Text, Link, Button, Notice, Spinner, Table, Column } from '@heartinternet/hi-ui';
import './SSLSummaryPage.css';

/** A single certificate as returned by GET /cp/rpc/rtr/ssl/list (overview_list). */
export interface SSLCertificate {
  common_name: string | null;
  display_name: string | null;
  created_date: string | null;
  /** Product/subscription expiry — the renewal date. */
  product_expiry: string | null;
  /** Actual issued-certificate expiry (RTR expiryDate); null until a cert is issued. */
  cert_expiry: string | null;
  is_installed: boolean;
  status: 'NEW' | 'PENDING' | 'ACTIVE' | 'EXPIRED';
  is_wildcard: boolean;
  oid: string;
  product_type: 'Simple' | 'Organisational' | 'Extended' | null;
}

interface RtrListResponse {
  data: SSLCertificate[] | null;
  error: string | null;
}

/** Shown when the request fails or returns an unusable (non-2xx / non-JSON) response. */
const GENERIC_FETCH_ERROR = 'There was a server error. Please contact support.';

export interface SSLSummaryPageProps {
  /** Endpoint returning the certificate list ({ data, error }). */
  fetchUrl?: string;
  /** Custom fetch function (defaults to window.fetch) — injectable for tests. */
  fetchFn?: typeof window.fetch;
  /** Base URL the Assign/Manage buttons navigate to (as `${sslInfoUrl}?id=<oid>`). */
  sslInfoUrl?: string;
  /** "Buy an SSL" link target shown in the empty state. */
  buySslUrl?: string;
  /** Custom CSS class */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

/**
 * Product display name — mirrors ws2k13/.../ssl/utilities/shared.js getCertificateName,
 * but only an explicit 'Extended' maps to Extended Validation, and Organisational is
 * shortened to "Org" to keep the Product column tidy. A null/unrecognised product_type
 * (e.g. a specific_type the backend couldn't classify) returns '' rather than being
 * mislabelled as an EV certificate.
 */
function getCertificateName(productType: string | null, isWildcard: boolean): string {
  if (productType === 'Simple') {
    return isWildcard ? 'Simple Wildcard' : 'Simple';
  }
  if (productType === 'Organisational') {
    return isWildcard ? 'Org Wildcard' : 'Org';
  }
  if (productType === 'Extended') {
    return 'Extended Validation';
  }
  return '';
}

/**
 * Status labels — the four statuses the list endpoint can return.
 * CANCELLED / RENEWED are filtered out server-side (overview_list), so they never reach here.
 */
const STATUS_LABELS: Record<SSLCertificate['status'], string> = {
  NEW: 'New',
  PENDING: 'Pending',
  ACTIVE: 'Active',
  EXPIRED: 'Expired',
};

/** dd/mm/yyyy, or '' when absent — mirrors ssl-summary.js formatDate. */
function formatDate(date: string | null): string {
  if (!date) return '';
  const parsed = new Date(date);
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
}

/** Newest first by created_date; nulls last — mirrors ssl-summary.js sortByDate. Default order. */
function sortByDate(data: SSLCertificate[]): SSLCertificate[] {
  return [...data].sort((a, b) => {
    if (!a.created_date) return 1;
    if (!b.created_date) return -1;
    return new Date(b.created_date).getTime() - new Date(a.created_date).getTime();
  });
}

/** Comparable value for a sortable column: lowercased string for text, epoch ms (or null) for dates. */
function sortValue(row: SSLCertificate, key: string): string | number | null {
  switch (key) {
    case 'name':
      return (row.display_name || row.common_name || '').toLowerCase();
    case 'product_type':
      return getCertificateName(row.product_type, row.is_wildcard).toLowerCase();
    case 'status':
      return STATUS_LABELS[row.status].toLowerCase();
    case 'cert_expiry':
      return row.cert_expiry ? new Date(row.cert_expiry).getTime() : null;
    case 'product_expiry':
      return row.product_expiry ? new Date(row.product_expiry).getTime() : null;
    /* v8 ignore next 2 -- every sortable column is handled above; default is unreachable */
    default:
      return null;
  }
}

export const SSLSummaryPage: React.FC<SSLSummaryPageProps> = ({
  fetchUrl = '/cp/rpc/rtr/ssl/list',
  fetchFn = window.fetch.bind(window),
  sslInfoUrl = '/cp/manage/sslinfo',
  buySslUrl = 'https://www.heartinternet.uk/ssl-certificates',
  className = '',
  style,
}) => {
  const [certificates, setCertificates] = useState<SSLCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const fetchFnRef = useRef(fetchFn);
  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  /* eslint-disable react-hooks/set-state-in-effect -- data-fetching effect legitimately sets loading state */
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchFnRef
      .current(fetchUrl, { signal: controller.signal })
      // A non-2xx response may still carry a JSON { error } worth surfacing, while
      // an HTML error page makes res.json() throw. Parse defensively and keep
      // res.ok so real backend messages survive and everything else falls back to
      // the generic message.
      .then((res) =>
        res
          .json()
          .then((body) => ({ ok: res.ok, body: body as RtrListResponse }))
          .catch(() => ({ ok: res.ok, body: null as RtrListResponse | null }))
      )
      .then(({ ok, body }) => {
        if (body?.error) {
          setError(body.error);
        } else if (!ok || !body) {
          setError(GENERIC_FETCH_ERROR);
        } else {
          setCertificates(sortByDate(body.data ?? []));
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setError(GENERIC_FETCH_ERROR);
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [fetchUrl]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const goToCertificate = (oid: string) => {
    window.location.href = `${sslInfoUrl}?id=${oid}`;
  };

  const handleSort = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDirection(direction);
  }, []);

  // Client-side sort (all rows are loaded at once). No column sorted → default created_date order.
  const sortedCertificates = useMemo(() => {
    if (!sortKey) return certificates;
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...certificates].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av === null && bv === null) return 0;
      if (av === null) return 1; // nulls last, regardless of direction
      if (bv === null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [certificates, sortKey, sortDirection]);

  const columns: Column<SSLCertificate>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (_value, row) => {
        if (row.display_name) {
          return (
            <span className="hi-ui-ssl-summary-page__name">
              <Text variant="inline">{row.display_name}</Text>
              <Text variant="inline" className="hi-ui-ssl-summary-page__common-name">
                {row.common_name}
              </Text>
            </span>
          );
        }
        if (row.common_name) {
          return <Text variant="inline">{row.common_name}</Text>;
        }
        return (
          <Text variant="inline" className="hi-ui-ssl-summary-page__unassigned">
            Unassigned
          </Text>
        );
      },
    },
    {
      key: 'product_type',
      label: 'Product',
      sortable: true,
      render: (_value, row) => getCertificateName(row.product_type, row.is_wildcard),
    },
    {
      key: 'cert_expiry',
      label: 'Cert reissues',
      sortable: true,
      render: (_value, row) => formatDate(row.cert_expiry),
    },
    {
      key: 'product_expiry',
      label: 'Prod renews',
      sortable: true,
      render: (_value, row) => formatDate(row.product_expiry),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_value, row) => (
        <Text variant="inline" className="hi-ui-ssl-summary-page__status">
          {STATUS_LABELS[row.status]}
        </Text>
      ),
    },
    {
      key: 'action',
      label: '',
      render: (_value, row) => {
        if (row.status === 'NEW') {
          return (
            <Button variant="success" fullWidth onClick={() => goToCertificate(row.oid)}>
              Assign
            </Button>
          );
        }
        if (row.status === 'PENDING' || row.status === 'ACTIVE') {
          return (
            <Button
              variant="warning"
              fullWidth
              className="hi-ui-ssl-summary-page__manage-button"
              onClick={() => goToCertificate(row.oid)}
            >
              Manage
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const classes = ['hi-ui-ssl-summary-page', className].filter(Boolean).join(' ');

  let list: React.ReactNode;
  if (loading) {
    list = (
      <div className="hi-ui-ssl-summary-page__message">
        <Spinner size="large" aria-label="Loading SSLs" />
        <Text variant="inline">Loading SSLs</Text>
      </div>
    );
  } else if (error) {
    list = (
      <div className="hi-ui-ssl-summary-page__message">
        <Text variant="inline" color="error">
          {error}
        </Text>
      </div>
    );
  } else if (certificates.length === 0) {
    list = (
      <div className="hi-ui-ssl-summary-page__message">
        <Text variant="inline">
          No SSL certificates found in our new SSL Platform. Buy one{' '}
          <Link href={buySslUrl}>here.</Link>
        </Text>
      </div>
    );
  } else {
    list = (
      <Table<SSLCertificate>
        columns={columns}
        data={sortedCertificates}
        gridColumns="1.5fr 1.2fr 1fr 1fr 0.9fr 150px"
        getRowKey={(row) => row.oid}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        ariaLabel="SSL certificates"
      />
    );
  }

  return (
    <div className={classes} style={style}>
      <Text variant="heading-1">SSL Certificates</Text>

      <Notice variant="info">
        <Text variant="inline">
          <strong>Certificate Reissue Date</strong> &ndash; The date your SSL certificate will be
          automatically revalidated and reissued. Although your SSL product runs for a full year,
          each certificate is valid for 200 days. We&rsquo;ll handle the reissue automatically, so
          you don&rsquo;t need to take any action.
          <br />
          <br />
          <strong>Product Renewal Date</strong> &ndash; The date your product is due to renew. This
          date determines when you will be billed.
        </Text>
      </Notice>

      <div className="hi-ui-ssl-summary-page__list">{list}</div>
    </div>
  );
};
